/**
 * props.ts — applyProps for compiled components.
 *
 * Applies a React-style props object to a DOM element, handling className,
 * style, event handlers, boolean attributes, refs, and children.
 *
 * Extracted from signals.ts for tree-shaking — only needed when compiled
 * output uses spread props ({...props}).
 */
import { applyStyles } from './styles.js';
import { applyRef } from './refs.js';
import { appendCompiled } from './append.js';
import { batch, createEffect, setClassName } from './signal-core.js';
// The VALUE-TYPE half of React's attribute rules (BUG-RUNTIME-128).
import { booleanPropWrite, sanitizeUrlProp } from '../../shared/attr-value-rules.js';
// The property-not-attribute rule for form-control props (BUG-RUNTIME-130).
import { writeFormControlProp } from './form-props.js';
import { syntheticize } from './synthetic-event.js';
// EPIC-MOON-002 R1: the style and ref arms are REGISTERED, not hard-wired.
// A direct import here pinned styles.js + refs.js into every bundle with a
// `{...rest}` spread, whether or not any spread could ever carry them. The
// compiler emits the arm calls in every file that uses __applyProps unless
// the whole-graph spread proof (compiler/spread-proof.cts) says the key can
// never occur; an unproven graph arms both — identical to the old behavior.
// EPIC-MOON-002 session 3: define-folded branch gates, same convention as
// signal-core's __IS_EXPORT__. The vite plugin defines these 'false' ONLY
// when the whole-graph word proof says the key class can never ride a
// spread (fully-scanned graph, netCanNeverFire bar); absent defines (jest,
// dev, unproven graphs) evaluate true and keep byte-identical behavior.
const __SPREAD_FORMPROPS__ = typeof __JETSHAKE_SPREAD_FORMPROPS__ === 'undefined' || __JETSHAKE_SPREAD_FORMPROPS__;
const __SPREAD_ONCHANGE__ = typeof __JETSHAKE_SPREAD_ONCHANGE__ === 'undefined' || __JETSHAKE_SPREAD_ONCHANGE__;
const __SPREAD_CHILDREN__ = typeof __JETSHAKE_SPREAD_CHILDREN__ === 'undefined' || __JETSHAKE_SPREAD_CHILDREN__;
const __SYNTHETIC_EVENTS__ = typeof __JETSHAKE_SYNTHETIC_EVENTS__ === 'undefined' || __JETSHAKE_SYNTHETIC_EVENTS__;
let _spreadStyles = null;
let _spreadRef = null;
export function _armSpreadStyle() { _spreadStyles = applyStyles; }
export function _armSpreadRef() { _spreadRef = applyRef; }
/**
 * IDL-REFLECTING PROPERTIES - the rule this function has now got wrong twice.
 *
 * Some DOM properties are the IDL reflection of a CONTENT attribute, so
 * ASSIGNING ONE CREATES THE ATTRIBUTE - even when what you assign is empty,
 * null or undefined. React guards every one of them behind a null check and
 * writes nothing when the prop is absent, so any unguarded assignment here
 * invents an attribute React does not emit. Both known instances came from a
 * third-party library spreading an explicit `undefined`:
 *
 *   el.defaultValue = ''    -> value=""   BUG-RUNTIME-065 (Base UI Field.Control)
 *   el.style.cssText = …    -> style=""   BUG-RUNTIME-093 (React Aria useRenderProps)
 *
 * 065 fixed the first and left the second in this same function, which is why
 * the pair is named here rather than at one arm: **before adding or editing any
 * arm below that assigns a PROPERTY rather than calling setAttribute, check
 * whether that property reflects an attribute, and if it does, return early on
 * `value == null`.** The current members are `style`, `value`, `defaultValue`
 * and `defaultChecked`; each carries a back-reference to this note.
 *
 * Guarded by testing/unit/boundary/style-attr-reflection.test.js and
 * testing/unit/boundary/defaultvalue-attr-reflection.test.js.
 */
function writePropOnce(el, key, value) {
    if (key === 'className') {
        setClassName(el, value);
        return;
    }
    if (key === 'style' && typeof value === 'object') {
        // `null` lands here too (typeof null === 'object') and applyStyles
        // early-returns on it, so a null style writes nothing. Measured, not
        // assumed - see testing/unit/boundary/style-attr-reflection.test.js.
        if (_spreadStyles)
            _spreadStyles(el, value);
        else
            console.error('[jetshake] spread style dropped — proof gap, please report');
        return;
    }
    // IDL-REFLECTING PROPERTY - see the note above writePropOnce. Assigning
    // cssText AT ALL materialises `style=""`, so an absent style must write
    // nothing (BUG-RUNTIME-093). React Aria's useRenderProps spreads
    // `style: undefined` onto every element that has no computed style.
    if (key === 'style') {
        if (value == null)
            return;
        el.style.cssText = value;
        return;
    }
    if (__SPREAD_FORMPROPS__) {
        // value / defaultValue / defaultChecked are DOM PROPERTIES, never
        // attributes (ATLAS-DOMWRITE-010). Falling through to the generic
        // setAttribute arm leaked a literal lowercase `defaultvalue=` attribute AND
        // left the field empty, because nothing ever reached the element's value
        // (BUG-RUNTIME-076). The rule now lives in ./form-props.js so the
        // dynamic/opaque-bag writer in render-component.ts asks the SAME question -
        // it never got 076's fix, which is BUG-RUNTIME-130. `value` on a non-form
        // element is not this rule's business, and the helper says so by declining
        // it; the historic property assignment below keeps that case unchanged.
        if (writeFormControlProp(el, key, value))
            return;
        if (key === 'value') {
            if (value == null)
                return;
            el[key] = value;
            return;
        }
        if (key === 'checked' || key === 'disabled' || key === 'selected') {
            el[key] = value;
            return;
        }
        if (key === 'htmlFor') {
            if (value == null)
                el.removeAttribute('for');
            else
                el.setAttribute('for', value);
            return;
        }
        if (key === 'dangerouslySetInnerHTML') {
            if (value && value.__html != null)
                el.innerHTML = value.__html;
            return;
        }
    }
    if (key.startsWith('on') && key.length > 2 && key[2] === key[2].toUpperCase()) {
        // React's onChange on text-like form controls maps to the native
        // 'input' event so it fires on every keystroke. Native 'change'
        // fires only on blur, which breaks controlled input patterns
        // (values land on the DOM element but never reach the signal
        // setter, so the signal-driven `value={getter}` createEffect
        // overwrites the typed value on the next re-render).
        let eventName = key.slice(2).toLowerCase();
        if (__SPREAD_ONCHANGE__ && eventName === 'change') {
            const tag = el.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
                const t = el.type;
                // Checkboxes and radios really do use 'change' in React too, because
                // their value toggles only at click time, not while typing.
                if (tag !== 'INPUT' || (t !== 'checkbox' && t !== 'radio')) {
                    eventName = 'input';
                }
            }
        }
        // BUG-RUNTIME-154: hand the handler React's synthetic surface, not the bare
        // native event. Gated + shakeable exactly like the branches above.
        el.addEventListener(eventName, (e) => batch(() => value(__SYNTHETIC_EVENTS__ ? syntheticize(e) : e)));
        return;
    }
    if (value === '' && typeof value === 'string' && ['inert'].includes(key)) {
        el.removeAttribute(key);
        return;
    }
    // BUG-COMPILER-045: xlink* props take the XLink NAMESPACE, mirroring the
    // compat writer in host-reconciler.ts - a plain setAttribute('xlinkHref', v)
    // on an SVG element writes a case-preserved literal that is not the XLink
    // href. Removal uses the QUALIFIED name, as React's
    // setValueForNamespacedAttribute does; the sanitizer is asked by PROP name
    // (only xlinkHref is on the list - BUG-RUNTIME-142).
    if (key.startsWith('xlink') && key.length > 5) {
        const xlinkAttr = 'xlink:' + key[5].toLowerCase() + key.slice(6);
        if (value == null || typeof value === 'boolean') {
            el.removeAttribute(xlinkAttr);
            return;
        }
        el.setAttributeNS('http://www.w3.org/1999/xlink', xlinkAttr, sanitizeUrlProp(key, String(value)));
        return;
    }
    if (typeof value === 'boolean') {
        // The VALUE-TYPE rule, asked once for every attribute (BUG-RUNTIME-128,
        // ATLAS-DOMWRITE-012 x 013). ARIA and data-* serialize as the string
        // "true"/"false" (React's setValueForAttribute exempts those two prefixes
        // from the boolean drop - BUG-SHADCN-022); the known boolean attributes
        // toggle the empty-string form; every OTHER name drops the value, which is
        // what `title={cond && s}` needs and what this arm used to write as
        // `title=""`.
        const disposition = booleanPropWrite(key);
        if (disposition === 'string') {
            el.setAttribute(key, String(value));
            return;
        }
        if (disposition === 'drop') {
            el.removeAttribute(key);
            return;
        }
        if (value)
            el.setAttribute(key, '');
        else
            el.removeAttribute(key);
        if (key in el)
            el[key] = value;
        return;
    }
    // BUG-RUNTIME-142: the `javascript:` URL rule (shared table) - a spread is
    // the likeliest carrier of an author- or CMS-supplied href.
    if (value != null && value !== false) {
        el.setAttribute(key, sanitizeUrlProp(key, String(value)));
    }
    else if (value == null || value === false) {
        if (el.hasAttribute(key))
            el.removeAttribute(key);
    }
}
/** The element's pre-spread value for a key, in the shape writePropOnce takes. */
function snapshotProp(el, key) {
    if (key === 'className')
        return el.getAttribute('class');
    if (key === 'style')
        return el.getAttribute('style');
    if (__SPREAD_FORMPROPS__ && key === 'htmlFor')
        return el.getAttribute('for');
    return el.getAttribute(key);
}
/** Put a shed key back the way it was before this spread first wrote it. */
function restoreProp(el, key, baseline) {
    if (baseline != null) {
        writePropOnce(el, key, baseline);
        return;
    }
    // null baseline: nothing held this key before, so React's merge leaves it
    // out entirely.
    if (key === 'className') {
        setClassName(el, null);
        return;
    }
    // IDL-REFLECTING PROPERTY - see the note above writePropOnce. Clearing to ''
    // is RIGHT when styles were actually written: React's setValueForStyles drops
    // a stale key with `style[key] = ''` and never calls removeAttribute, so an
    // element that carried inline styles keeps an empty `style` attribute once
    // they are cleared, and matching React means keeping ours. But cssText is
    // still an IDL reflection, so doing it unconditionally INVENTS `style=""` on
    // an element that never carried one - which is BUG-RUNTIME-093 arriving by
    // the shed path instead of the write path. Only clear what exists.
    if (key === 'style') {
        if (el.hasAttribute('style'))
            el.style.cssText = '';
        return;
    }
    if (__SPREAD_FORMPROPS__ && key === 'htmlFor') {
        el.removeAttribute('for');
        return;
    }
    // `disabled`/`selected` and friends are reflected IDL attributes: removing
    // the content attribute resets the property too, so one call covers both.
    el.removeAttribute(key);
}
/**
 * Keys whose disappearance from a spread has no well-defined DOM undo, so the
 * memory never records them:
 *   - `on*` handlers: writePropOnce wrapped the listener in an anonymous
 *     arrow, so there is no reference to removeEventListener with.
 *   - value/checked/default*: React turns the control UNCONTROLLED when the
 *     prop disappears and leaves the live DOM value where it is, so clearing
 *     them would be a bigger lie than doing nothing.
 *   - key/ref/children: not DOM state.
 */
function isUntrackableSpreadKey(key) {
    return (key === 'key' || key === 'ref' || key === 'children' ||
        key === 'value' || key === 'defaultValue' ||
        key === 'checked' || key === 'defaultChecked' ||
        key === 'dangerouslySetInnerHTML' ||
        (key.startsWith('on') && key.length > 2 && key[2] === key[2].toUpperCase()));
}
/**
 * Un-apply every remembered key the new props object no longer carries.
 * `props` null means the spread evaluated to nothing this run, so everything
 * it held goes back.
 */
function shedMissing(el, memo, props) {
    for (const k in memo) {
        if (props && k in props)
            continue;
        restoreProp(el, k, memo[k]);
        delete memo[k];
    }
}
/**
 * applySelectSpreadValue — a <select>'s value/defaultValue that arrived through
 * a `{...props}` SPREAD, re-applied once the <option> children exist.
 *
 * `el.value = 'b'` against an empty option list is a silent no-op, and
 * applyProps runs before the children are appended. The compiler emits a call
 * to this from selectDeferredStatements (after the children) for every
 * `<select {...x}>`; when the spread carries neither key it returns having
 * touched nothing.
 */
export function applySelectSpreadValue(el, props) {
    if (!props || typeof props !== 'object')
        return;
    let v = props.value;
    let isDefault = false;
    if (v == null) {
        v = props.defaultValue;
        isDefault = true;
    }
    // Reactive props arrive as getters (the compiler's getter discipline).
    if (typeof v === 'function')
        v = v();
    if (v == null)
        return;
    el.value = v;
    // React's updateOptions marks the chosen option's defaultSelected when the
    // selection came from a defaultValue (setDefaultSelected), which REFLECTS
    // the `selected` attribute. A controlled value only moves the property.
    if (isDefault) {
        const opts = el.options;
        for (let i = 0; i < opts.length; i++) {
            if (opts[i].selected)
                opts[i].setAttribute('selected', '');
            else
                opts[i].removeAttribute('selected');
        }
    }
}
/**
 * applyProps — apply a props object to a DOM element.
 * Handles className, style, event handlers, and generic attributes.
 *
 * `refOrKeys` carries two mutually exclusive call shapes, so a reactive spread
 * costs no extra argument slot:
 *   - `true` — return the spread's ref instead of firing it (BUG-DIFF-003).
 *   - SpreadKeyMemo — the per-call-site key memory of a REACTIVE spread. The
 *     compiler allocates one per spread site OUTSIDE its createEffect; each run
 *     applies the new object, then un-applies whatever keys the previous run
 *     wrote and this one dropped (BUG-COMPILER-033).
 * The two never meet: only the non-reactive emit path asks for the ref back.
 */
export function applyProps(el, props, refOrKeys) {
    const memo = typeof refOrKeys === 'object' && refOrKeys !== null ? refOrKeys : null;
    if (!props || typeof props !== 'object') {
        // A spread that evaluated to nothing this run still has to un-apply the
        // keys the previous run wrote.
        if (memo)
            shedMissing(el, memo, null);
        return undefined;
    }
    let deferredRef = undefined;
    for (const key of Object.keys(props)) {
        if (key === 'key')
            continue;
        // Snapshot the pre-spread state the FIRST time this site holds a key —
        // before any write below, so a key that comes and goes always falls back
        // to the same earlier-layer value.
        if (memo && !(key in memo) && !isUntrackableSpreadKey(key))
            memo[key] = snapshotProp(el, key);
        // React: a control carrying BOTH value and defaultValue is controlled -
        // initInput picks `value` and the defaultValue is never read. Skipping the
        // key here keeps that precedence whatever order the spread enumerates in.
        if (__SPREAD_FORMPROPS__ && key === 'defaultValue' && props.value != null)
            continue;
        if (key === 'children') {
            // When a compiled component spreads {...props} onto a host element,
            // children from the parent must be appended to the DOM element.
            // Function children are lazy getters emitted by the compiler —
            // wrap in createEffect so the subtree rebuilds on signal change.
            // Define-folded (EPIC-MOON-002 s3): the AST proof
            // (scanSpreadsCannotCarryChildren) clears this branch only when every
            // JSX spread site in the graph provably excludes a children key.
            if (__SPREAD_CHILDREN__) {
                const ch = props.children;
                if (typeof ch === 'function') {
                    const startMarker = document.createComment('');
                    const endMarker = document.createComment('');
                    el.appendChild(startMarker);
                    el.appendChild(endMarker);
                    let currentNodes = [];
                    createEffect(() => {
                        for (const n of currentNodes) {
                            if (n.parentNode)
                                n.parentNode.removeChild(n);
                        }
                        currentNodes = [];
                        const result = ch();
                        const frag = document.createDocumentFragment();
                        appendCompiled(frag, result);
                        currentNodes = Array.from(frag.childNodes);
                        el.insertBefore(frag, endMarker);
                    });
                }
                else {
                    appendCompiled(el, ch);
                }
            }
            else {
                console.error('[jetshake] spread children dropped — proof gap, please report');
            }
            continue;
        }
        if (key === 'ref') {
            // Defer ref until after all other props are set,
            // so ref callback can read attributes like id, className, etc.
            deferredRef = props[key];
            continue;
        }
        const value = props[key];
        // Function-valued non-event props are reactive getters. Wire each via
        // createEffect so caller-side signal changes propagate to the DOM attribute
        // without remounting the component. Event handlers (on*) stay as functions
        // and take the handler-branch below.
        const isEventHandler = key.startsWith('on') && key.length > 2 && key[2] === key[2].toUpperCase();
        if (typeof value === 'function' && !isEventHandler && key !== 'ref' && key !== 'dangerouslySetInnerHTML') {
            createEffect(() => {
                const resolved = value();
                writePropOnce(el, key, resolved);
            });
            continue;
        }
        writePropOnce(el, key, value);
    }
    // Reactive spread: un-apply whatever the PREVIOUS object carried and this
    // one does not. React re-diffs the merged props every render, so a shed key
    // falls back to the rest of the merge; applying key-by-key without this left
    // the stale value on the element forever.
    if (memo)
        shedMissing(el, memo, props);
    // Apply ref after all props so callback can read fully-initialized element.
    // When returnRef is set, the caller (compiler-emitted host construction) needs
    // to fire the spread's ref even LATER — after static props it emits AFTER this
    // applyProps call (e.g. `el.defaultValue = 'Ada'`). Hand the ref back so it
    // lands in the element's deferredRefStatements and fires last, matching React's
    // ref-after-all-props order. Critical for react-hook-form: register()'s ref
    // must fire after defaultValue so the input's live `.value` reflects it —
    // otherwise the ref adopts the empty pre-defaultValue value and submit-time
    // collection reads "" (BUG-DIFF-003).
    if (deferredRef !== undefined) {
        if (refOrKeys === true)
            return deferredRef;
        if (_spreadRef)
            _spreadRef(el, deferredRef);
        else
            console.error('[jetshake] spread ref dropped — proof gap, please report');
    }
    return undefined;
}
